import BaseDialogCmp from "../../../components/BaseDialog/BaseDialogCmp.js";
import {safeText} from "../../util/CommonUtils.js";
import DownloadErrorDialog from "../../../components/DownloadErrorDialog/DownloadErrorDialog.js";
import HtmlUtils from "../../../components/HtmlUtils.js";

export default class HttpErrorHandler {
    static #GENERIC_MESSAGE = "Something went wrong, please try again. If this keeps happening, please report it.";
    /**
     * @type {BaseDialogCmp}
     */
    #signInPopup;
    /**
     * @type {BaseDialogCmp}
     */
    #accessDeniedPopup;
    /**
     * @type {BaseDialogCmp}
     */
    #stackTracePopup;
    /**
     * @type {BaseDialogCmp}
     */
    #notFoundPopup;
    /**
     * @type {BaseDialogCmp}
     */
    #internalServerPopup;

    constructor() {
        this.#signInPopup = new BaseDialogCmp({btnCancel: "Close", btnOk: "Sign in"});
        this.#accessDeniedPopup = new BaseDialogCmp({btnCancel: null, btnOk: "Refresh"});
        this.#stackTracePopup = new BaseDialogCmp({btnCancel: "Close", btnOk: null});
        this.#notFoundPopup = new BaseDialogCmp({btnCancel: null, btnOk: "Refresh"});
        this.#internalServerPopup = new BaseDialogCmp({btnSecondary: "Submit", btnOk: "Close", btnCancel: null});
    }

    /**
     * @param {HttpError} error
     */
    handle(error) {
        if (error.isUserSessionExpired)
            this.#handleSessionExpired(error);
        else if (error.isUnauthorized)
            this.#handleUnauthorized(error);
        else if (error.isForbidden)
            this.#handleForbidden(error);
        else if (error.isBadRequest)
            this.#handleBadRequest(error)
        else if (error.isInternalServerError)
            this.handleInternalServerError(error.errorMessage, error.responseJson.stackTrace,
                error.securityContext.getCurrentUser());
        else if (error.isNotFound)
            this.#handleIsNotFound(error)
        else if (error.isTooManyRequestsError)
            this.#handleTooManyRequests(error)
        else if (error.isLicenseRequired)
            this.#handleLicenseRequired(error)
    }

    /**
     * @param {string} errorMessage
     * @param {string} stackTrace
     * @param {UserWithOrgs} user
     */
    handleInternalServerError(errorMessage, stackTrace, user ) {
        if (this.#internalServerPopup.isAlreadyShown())
            return;
        if (errorMessage) {
            this.#internalServerPopup.showText(errorMessage)
        } else if (stackTrace) {
            this.#internalServerPopup.showErrorStackTrace(stackTrace);
        } else {// the server didn't answer with our usual JSON error (a proxy/Tomcat error, an empty body)
            this.#internalServerPopup.showText(HttpErrorHandler.#GENERIC_MESSAGE);
        }
        this.#internalServerPopup.onSecondary(this.#showDownloadErrorDialog.bind(this, errorMessage, stackTrace,
            user))
    }

    /**
     * Page loads & we're anonymous
     * GET returns 401 -> You need to sign in (button: Sign in).
     *
     * Page loads, we're logged out in the process
     * GET/PUT/PATCH/POST return 401 -> Your session has expired. (button: Sign in)
     *
     * @param {HttpError} error
     */
    #handleUnauthorized(error) {
        if (this.#signInPopup.isAlreadyShown())
            return;
        this.#signInPopup.onOk(() => {
            window.location.href = error.securityContext.signInUrl;
        });
        this.#signInPopup.showText("You need to sign in.");
    }

    /**
     * We're logged in, but don't have access to the resource - either we never had it, or it was revoked while
     * we were using the page. Either way the page shows stale data, so the only useful action is to reload it.
     *
     * @param {HttpError} error
     */
    #handleForbidden(error) {
        if (this.#accessDeniedPopup.isAlreadyShown())
            return;
        this.#accessDeniedPopup.onOk(() => {
            location.reload();
        });
        const errorDetails = error.errorMessage ? ` Error details: ${error.errorMessage}` : '';
        this.#accessDeniedPopup.showText("Your permissions have changed and aren't sufficient to perform the " +
            `operation anymore. Refresh the page to see the up-to-date information.${errorDetails}`);
    }

    #handleBadRequest(error) {
        if (this.#stackTracePopup.isAlreadyShown())
            return;
        // It's possible, that response could be a JSON but as text, this could happen when
        // we expect text/plain as response, so we use responseJson instead of regular response
        let response = error.responseJson;
        if (response.errors?.length) {
            this.#stackTracePopup.showHtml(this.#badRequestTemplate(response.errors));
            return;
        }
        let message = error.errorMessage || error.responseJson.stackTrace;
        this.#stackTracePopup.showText(message || HttpErrorHandler.#GENERIC_MESSAGE);
    }

    #handleIsNotFound(error) {
        if (this.#notFoundPopup.isAlreadyShown())
            return;
        this.#notFoundPopup.onOk(() => {
            location.reload()
        });
        this.#notFoundPopup.showText(error.errorMessage ||
            "The data doesn't exist anymore, it must have been deleted. Refresh the page to see the up-to-date information.");
    }

    #handleSessionExpired(error) {
        if (this.#signInPopup.isAlreadyShown())
            return;
        this.#signInPopup.onOk(() => {
            window.location.href = error.securityContext.signInUrl;
        });
        this.#signInPopup.showText("Your session has expired.");
    }

    #handleTooManyRequests(error) {
        if (this.#stackTracePopup.isAlreadyShown())
            return;
        this.#stackTracePopup.showText("Too many people are processing their data right now, try again later.");
    }

    #handleLicenseRequired(error) {
        const pageContent = document.querySelector('.page-header + *') || document.body;
        HtmlUtils.onPageDataAccessExceptionThrown(pageContent, error);
    }

    /** @param {{detailedMessage: string, message: string, field: string}[]}errors */
    #badRequestTemplate(errors) {
        return `
            <ul>
                ${errors.map(e => {
                    const text = e.detailedMessage || e.message
                    return `<li class="bad-request-dialog__list-item">${safeText(text)}</li>`
                }).join('')}
            </ul>`

    }

    /**
     * @param {string} errorMessage
     * @param {string} errorText
     * @param {UserWithOrgs} user
     */
    #showDownloadErrorDialog(errorMessage, errorText, user){
        const dialog = new DownloadErrorDialog();
        dialog.show(errorMessage, errorText, user)
    }
}